import { useState, useEffect } from 'react'
import { Box, VStack, Text, toaster, useT } from '@minetrace/ui'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm, useFormColors, FormHeader, WizardActions, WizardStepper } from '@minetrace/ui/forms'
import { FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { programacionesService } from '../../api/services'
import { useRequest } from '../../hooks'
import type { ScheduleResponse, ScheduleGeologistResponse, ScheduleDetailResponse, ScheduleDocumentResponse } from '../../api/models/response/programacion'
import Step1DatosGenerales, { type PendingFile } from './steps/Step1DatosGenerales'
import Step2Geologos from './steps/Step2Geologos'
import Step3DerechosMineros, { type SelectedMiningRight } from './steps/Step3DerechosMineros'

type TabType = 'derechos' | 'general' | 'geologos'

const TAB_ORDER: TabType[] = ['derechos', 'general', 'geologos']

const today = new Date().toISOString().split('T')[0]

const generalSchema = z.object({
  name: z.string().optional(),
  startDate: z.string().min(1, 'programaciones.wizard.zod.startDateRequired'),
  endDate: z.string().min(1, 'programaciones.wizard.zod.endDateRequired'),
  includeOcdeAudit: z.boolean(),
  ocdeAuditRequired: z.boolean(),
}).refine((data) => data.startDate >= today, {
  message: 'programaciones.wizard.zod.startDateNotPast',
  path: ['startDate'],
}).refine((data) => data.endDate >= data.startDate, {
  message: 'programaciones.wizard.zod.endDateAfterStart',
  path: ['endDate'],
})

type GeneralFormData = z.infer<typeof generalSchema>

export default function NuevaProgramacionPage() {
  const navigate = useNavigate()
  const c = useFormColors()
  const { t } = useT()
  const { id } = useParams()
  const isEditing = Boolean(id)

  const [activeTab, setActiveTab] = useState<TabType>('derechos')
  const [loadingSchedule, setLoadingSchedule] = useState(isEditing)
  const [completedSteps, setCompletedSteps] = useState<Set<TabType>>(new Set())
  const [scheduleId, setScheduleId] = useState<string | null>(id ?? null)
  const [scheduleCode, setScheduleCode] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  // Step 1 (derechos): Mining rights selection (full data so cards/map survive tab navigation)
  const [selectedMiningRights, setSelectedMiningRights] = useState<SelectedMiningRight[]>([])
  // Contact references per detail (miningRightId -> contactReference)
  const [contactReferences, setContactReferences] = useState<Map<string, string>>(new Map())

  // Step 2 (general): persisted details from backend
  const [details, setDetails] = useState<ScheduleDetailResponse[]>([])
  const [documents, setDocuments] = useState<ScheduleDocumentResponse[]>([])
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([])

  // Step 3 (geologos)
  const [geologists, setGeologists] = useState<ScheduleGeologistResponse[]>([])

  const { execute: execFinalize } = useRequest(
    async (schedId: string) => {
      await programacionesService.finalize(schedId)
      return true as const
    },
    { successMessage: t('programaciones.wizard.toasts.finalizeSuccess') },
  )

  const formMethods = useForm<GeneralFormData>({
    resolver: zodResolver(generalSchema),
    defaultValues: { name: '', startDate: '', endDate: '', includeOcdeAudit: false, ocdeAuditRequired: false },
  })
  const {
    control,
    trigger,
    getValues,
    reset,
    formState: { errors },
  } = formMethods

  // Load existing schedule for edit mode
  useEffect(() => {
    if (!id) return
    const load = async () => {
      setLoadingSchedule(true)
      try {
        const data = await programacionesService.getById(id)
        populateFromResponse(data)
      } catch {
        // silently fail
      } finally {
        setLoadingSchedule(false)
      }
    }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const populateFromResponse = (data: ScheduleResponse) => {
    reset({
      name: data.name ?? '',
      startDate: data.startDate,
      endDate: data.endDate,
      includeOcdeAudit: data.includeOcdeAudit ?? false,
      ocdeAuditRequired: data.ocdeAuditRequired ?? false,
    })
    setScheduleId(data.id)
    setScheduleCode(data.code)
    setGeologists(data.geologists ?? [])
    setDetails(data.details ?? [])
    setDocuments(data.documents ?? [])

    // Restore mining right selection and contact references from details
    const refs = new Map<string, string>()
    const items: SelectedMiningRight[] = []
    for (const d of data.details ?? []) {
      items.push({
        miningRightId: d.miningRightId,
        uniqueCode: d.miningRightCode ?? '',
        name: d.miningRightName,
        companyId: d.companyId,
        companyName: d.companyName,
        companyRuc: '',
        miningRightStatusId: 0,
        geoJson: d.boundaryGeoJson ?? null,
        substance: null,
      })
      if (d.contactReference) refs.set(d.miningRightId, d.contactReference)
    }
    setSelectedMiningRights(items)
    setContactReferences(refs)

    // Restore wizard state
    if (data.scheduleStatus >= 1) {
      setCompletedSteps(new Set(TAB_ORDER))
    } else {
      // If has details, derechos is done. If has dates, general is done.
      const completed = new Set<TabType>()
      if ((data.details ?? []).length > 0) completed.add('derechos')
      if (data.startDate) completed.add('general')
      setCompletedSteps(completed)

      // Set active tab to first incomplete
      if (!completed.has('derechos')) setActiveTab('derechos')
      else if (!completed.has('general')) setActiveTab('general')
      else setActiveTab('geologos')
    }
  }

  // Wizard step handler
  const handleWizardStep = async () => {
    setIsSaving(true)
    try {
      if (activeTab === 'derechos') {
        if (selectedMiningRights.length === 0) {
          toaster.error({ description: t('programaciones.wizard.toasts.selectAtLeastOneMiningRight') })
          return
        }
        setCompletedSteps((prev) => new Set(prev).add('derechos'))
        setActiveTab('general')
      } else if (activeTab === 'general') {
        const data = getValues()
        const filesToUpload = pendingFiles.length > 0
          ? pendingFiles.map((pf) => ({ file: pf.file, notes: pf.notes }))
          : undefined
        const result = await programacionesService.createStep1(
          {
            id: scheduleId ?? undefined,
            name: data.name || undefined,
            startDate: data.startDate,
            endDate: data.endDate,
            includeOcdeAudit: data.includeOcdeAudit,
            ocdeAuditRequired: data.includeOcdeAudit && data.ocdeAuditRequired,
          },
          filesToUpload,
        )
        if (!result) return
        setScheduleId(result.id)
        if (result.code) setScheduleCode(result.code)
        if (result.documents) setDocuments(result.documents)
        setPendingFiles([])
        setCompletedSteps((prev) => new Set(prev).add('general'))
        setActiveTab('geologos')
      } else if (activeTab === 'geologos') {
        if (geologists.length === 0) {
          toaster.error({ description: t('programaciones.wizard.toasts.selectAtLeastOneGeologist') })
          return
        }
        if (scheduleId) {
          // Sync mining rights with backend
          const currentDetailMrIds = new Set(details.map((d) => d.miningRightId))
          const selectedMrIds = new Set(selectedMiningRights.map((s) => s.miningRightId))

          for (const detail of details) {
            if (!selectedMrIds.has(detail.miningRightId)) {
              await programacionesService.removeDetail(scheduleId, detail.id)
            }
          }

          const newDetails: ScheduleDetailResponse[] = []
          for (const item of selectedMiningRights) {
            if (!currentDetailMrIds.has(item.miningRightId)) {
              const ref = contactReferences.get(item.miningRightId)
              const result = await programacionesService.addDetail(scheduleId, item.miningRightId, item.companyId, ref)
              newDetails.push(result)
            }
          }

          // Update contact references for existing details
          const keptDetails = details.filter((d) => selectedMrIds.has(d.miningRightId))
          for (const detail of keptDetails) {
            const ref = contactReferences.get(detail.miningRightId) ?? ''
            if (ref !== (detail.contactReference ?? '')) {
              await programacionesService.updateDetailReference(scheduleId, detail.id, ref)
            }
          }

          setDetails([...keptDetails, ...newDetails])

          // Finalize
          const finalizeResult = await execFinalize(scheduleId)
          if (!finalizeResult) return
          setCompletedSteps((prev) => new Set(prev).add('geologos'))
          setTimeout(() => navigate('/app/programaciones'), 1500)
        }
      }
    } finally {
      setIsSaving(false)
    }
  }

  // Tab navigation guard
  const handleTabChange = (tab: TabType) => {
    if (isEditing) {
      setActiveTab(tab)
      return
    }
    const tabIndex = TAB_ORDER.indexOf(tab)
    const currentIndex = TAB_ORDER.indexOf(activeTab)
    if (tabIndex <= currentIndex) {
      setActiveTab(tab)
      return
    }
    if (completedSteps.has(TAB_ORDER[tabIndex - 1]!)) {
      setActiveTab(tab)
    }
  }

  const handleBack = () => {
    const idx = TAB_ORDER.indexOf(activeTab)
    if (idx > 0) setActiveTab(TAB_ORDER[idx - 1]!)
  }

  const handleStepSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (activeTab === 'general') {
      const isValid = await trigger(['startDate', 'endDate'])
      if (!isValid) return
    }
    await handleWizardStep()
  }

  const tabs = [
    { key: 'derechos' as const, label: t('programaciones.wizard.tabs.derechos', { count: selectedMiningRights.length }), hasError: false },
    { key: 'general' as const, label: t('programaciones.wizard.tabs.general'), hasError: Boolean(errors.startDate || errors.endDate) },
    { key: 'geologos' as const, label: t('programaciones.wizard.tabs.geologos', { count: geologists.length }), hasError: false },
  ]

  if (loadingSchedule) {
    return (
      <VStack gap={6} alignItems="stretch">
        <FormHeader title={t('programaciones.wizard.page.loadingTitle')} subtitle={t('programaciones.wizard.page.loadingSubtitle')} backPath="/app/programaciones" />
        <Box p={8} textAlign="center">
          <Text tid="programaciones.wizard.page.loading" color={c.textMuted} />
        </Box>
      </VStack>
    )
  }

  return (
    <VStack gap={6} alignItems="stretch">
      <FormHeader
        title={isEditing ? t('programaciones.wizard.page.titleEdit') : t('programaciones.wizard.page.titleNew')}
        subtitle={isEditing ? t('programaciones.wizard.page.subtitleEdit') : t('programaciones.wizard.page.subtitleNew')}
        backPath="/app/programaciones"
      />

      <Box bg="white" borderRadius="xl" border="1px solid" borderColor="#e8dfd1" px={4} py={2} shadow="0 1px 3px rgba(0,0,0,0.04)">
        <WizardStepper steps={tabs} activeStep={activeTab} completedSteps={completedSteps} onStepClick={handleTabChange} />
      </Box>

      <FormProvider {...formMethods}>
        <form onSubmit={handleStepSubmit}>
        {activeTab === 'derechos' && (
          <Step3DerechosMineros
            selectedItems={selectedMiningRights}
            onSelectedItemsChange={setSelectedMiningRights}
            contactReferences={contactReferences}
            onContactReferenceChange={(mrId, val) => {
              setContactReferences((prev) => {
                const next = new Map(prev)
                if (val) next.set(mrId, val)
                else next.delete(mrId)
                return next
              })
            }}
          />
        )}

        {activeTab === 'general' && (
          <Step1DatosGenerales
            control={control}
            errors={errors}
            code={scheduleCode}
            scheduleId={scheduleId}
            documents={documents}
            onDocumentsChange={setDocuments}
            pendingFiles={pendingFiles}
            onPendingFilesChange={setPendingFiles}
          />
        )}

        {activeTab === 'geologos' && (
          <Step2Geologos
            scheduleId={scheduleId}
            geologists={geologists}
            onGeologistsChange={setGeologists}
          />
        )}

        <WizardActions
          cancelPath="/app/programaciones"
          isFirst={activeTab === 'derechos'}
          isLast={activeTab === 'geologos'}
          isEditing={false}
          isSubmitting={isSaving}
          onBack={handleBack}
        />
        </form>
      </FormProvider>
    </VStack>
  )
}
